/**
 * Comprehensive API Error Handler for Production Issues
 * Handles missing endpoints, authentication errors, and mixed content issues
 */

import { debugLogger } from '../components/debug/DataLoadingDebugger';
import { authEvents } from './authEvents';

export interface APIError {
  status: number;
  message: string;
  endpoint: string;
  method: string;
  isHandled: boolean;
}

// Known missing endpoints that should be handled gracefully
const MISSING_ENDPOINTS = [
  '/api/v1/doctor-offices',
  '/api/v1/doctor-offices/assignments',
  '/api/v1/ultrasound-machines/my-machines',
  '/api/v1/worklist/items',
  '/api/v1/dicom/servers',
  '/api/v1/system/health'
];

// Known problematic endpoints that return 500 errors
const PROBLEMATIC_ENDPOINTS = [
  '/api/v1/ultrasound-machines/validate-ae-title'
];

// Handle 500 internal server errors
export const handle500Error = (error: any): APIError => {
  const endpoint = error.config?.url || '';
  const method = error.config?.method?.toUpperCase() || '';
  
  // Check if it's a known problematic endpoint
  const isProblematicEndpoint = PROBLEMATIC_ENDPOINTS.some(problematic => 
    endpoint.includes(problematic)
  );
  
  if (isProblematicEndpoint) {
    debugLogger.log('warn', 'Server Error', `${method} ${endpoint} - Backend implementation issue`, {
      endpoint,
      method,
      status: 500,
      suggestion: 'This endpoint has backend issues and should be handled gracefully'
    });
    
    return {
      status: 500,
      message: 'Esta funcionalidad está experimentando problemas técnicos',
      endpoint,
      method,
      isHandled: true
    };
  }
  
  return {
    status: 500,
    message: 'Error interno del servidor',
    endpoint,
    method,
    isHandled: false
  };
};

// Handle missing endpoints gracefully
export const handleMissingEndpoint = (endpoint: string, method: string): APIError => {
  const isMissingEndpoint = MISSING_ENDPOINTS.some(missing => endpoint.includes(missing));
  
  if (isMissingEndpoint) {
    debugLogger.log('warn', 'Missing Endpoint', `${method} ${endpoint} - Not yet implemented`, {
      endpoint,
      method,
      suggestion: 'This endpoint is not yet available on the backend'
    });
    
    return {
      status: 404,
      message: 'Esta funcionalidad aún no está disponible',
      endpoint,
      method,
      isHandled: true
    };
  }
  
  return {
    status: 404,
    message: 'Endpoint no encontrado',
    endpoint,
    method,
    isHandled: false
  };
};

// Public auth endpoints where 401 is an expected credential/verification failure, not session expiry
const PUBLIC_AUTH_401_ENDPOINTS = [
  '/api/v1/auth/login',
  '/api/v1/auth/login-with-profile',
  '/api/v1/auth/register',
  '/api/v1/auth/forgot-password',
  '/api/v1/auth/resend-verification',
  '/api/v1/auth/verify-email',
  '/api/v1/auth/reset-password',
];

const isPublicAuthEndpoint = (url: string = ''): boolean =>
  PUBLIC_AUTH_401_ENDPOINTS.some((endpoint) => url.includes(endpoint));

// Handle authentication errors
export const handleAuthError = (error: any): boolean => {
  if (error.response?.status === 401) {
    const endpoint = error.config?.url || '';
    if (isPublicAuthEndpoint(endpoint)) {
      return false;
    }

    debugLogger.log('error', 'Authentication', 'Session expired or invalid token');

    localStorage.removeItem('auth_token');
    authEvents.emitSessionExpired();

    setTimeout(() => {
      window.location.href = '/auth/login';
    }, 1000);

    return true;
  }

  if (error.response?.status === 400 &&
      error.response?.data?.detail?.includes('Unable to determine user ID')) {
    debugLogger.log('error', 'Authentication', 'Token malformed or user ID missing');

    localStorage.removeItem('auth_token');
    authEvents.emitSessionExpired();

    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');

    setTimeout(() => {
      window.location.href = '/auth/login';
    }, 1000);

    return true;
  }
  
  return false;
};

// Handle HTTPS/HTTP mixed content errors
export const handleMixedContentError = (error: any): boolean => {
  const isNetworkError = !error.response && error.request;
  const currentProtocol = window.location.protocol;
  const requestURL = error.config?.url || '';
  
  // Check if the request URL is a full HTTP URL from HTTPS page
  const isFullHTTPUrl = requestURL.startsWith('http://');
  
  // Only flag as mixed content if we can clearly identify HTTP requests from HTTPS page
  if (isNetworkError && currentProtocol === 'https:' && isFullHTTPUrl) {
    debugLogger.log('error', 'Mixed Content', 'HTTP request from HTTPS page detected', {
      baseURL: error.config?.baseURL,
      url: error.config?.url,
      currentProtocol: window.location.protocol,
      suggestion: 'Ensure all API calls use HTTPS in production'
    });
    
    return true;
  }
  
  // For other network errors, don't assume mixed content
  if (isNetworkError && currentProtocol === 'https:') {
    debugLogger.log('error', 'Network Error', 'Network request failed on HTTPS page', {
      baseURL: error.config?.baseURL,
      url: error.config?.url,
      currentProtocol: window.location.protocol,
      message: error.message,
      suggestion: 'Check backend connectivity and CORS configuration'
    });
    
    return false; // Don't treat as mixed content
  }
  
  return false;
};

// Main error handler
export const handleAPIError = (error: any): APIError | null => {
  // Handle authentication errors first
  if (handleAuthError(error)) {
    return {
      status: error.response?.status || 401,
      message: 'Error de autenticación',
      endpoint: error.config?.url || '',
      method: error.config?.method || '',
      isHandled: true
    };
  }
  
  // Handle 500 internal server errors
  if (error.response?.status === 500) {
    return handle500Error(error);
  }
  
  // Handle missing endpoints
  if (error.response?.status === 404) {
    return handleMissingEndpoint(
      error.config?.url || '',
      error.config?.method?.toUpperCase() || ''
    );
  }
  
  // Handle mixed content issues
  if (handleMixedContentError(error)) {
    return {
      status: 0,
      message: 'Error de conexión - problema de contenido mixto',
      endpoint: error.config?.url || '',
      method: error.config?.method || '',
      isHandled: true
    };
  }
  
  // Log unhandled errors
  debugLogger.log('error', 'Unhandled API Error', 'Unknown error type', {
    status: error.response?.status,
    message: error.message,
    endpoint: error.config?.url,
    method: error.config?.method,
    data: error.response?.data
  });
  
  return null;
};

// User-friendly error messages
export const getErrorMessage = (error: APIError): string => {
  switch (error.status) {
    case 400:
      return 'Solicitud inválida. Por favor, verifica los datos ingresados.';
    case 401:
      return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
    case 403:
      return 'No tienes permisos para realizar esta acción.';
    case 404:
      return error.isHandled ? error.message : 'El recurso solicitado no fue encontrado.';
    case 500:
      return error.isHandled ? error.message : 'Error interno del servidor. Por favor, intenta más tarde.';
    default:
      return 'Ocurrió un error inesperado. Por favor, intenta más tarde.';
  }
};

// Production environment checker
export const checkProductionConfig = (): void => {
  const isProduction = (import.meta as any).env?.PROD || false;
  const apiBaseURL = (import.meta as any).env?.VITE_API_BASE_URL || '';
  const currentProtocol = window.location.protocol;
  
  debugLogger.log('info', 'Environment Check', 'Production configuration', {
    isProduction,
    apiBaseURL,
    currentProtocol,
    host: window.location.host
  });
  
  // Warn about HTTP in production
  if (isProduction && currentProtocol === 'http:') {
    debugLogger.log('warn', 'Security', 'Running on HTTP in production - this may cause issues');
  }
  
  // Warn about missing API URL
  if (!apiBaseURL) {
    debugLogger.log('warn', 'Configuration', 'VITE_API_BASE_URL not set, using fallback');
  }
};
